// @ts-strict-ignore
/*
 *
 *   ███████╗████████╗  █████ █╗  ██████╗
 *   ██╔════╝ ╚══██╔══╝ ██╔═══██╗ ██╔══██╗
 *   ███████╗    ██║    ██║   ██║ ██████╔╝
 *   ╚════██║    ██║    ██║   ██║ ██╔═══╝
 *   ███████║    ██║    ╚██████╔╝ ██║
 *   ╚══════╝    ╚═╝     ╚═════╝  ╚═╝
 *
 * If you are making edits to this file,
 * ASK IN #ask-aviary-engineering FIRST.
 *
 * This file basically just wraps the new AviaryProvider from @fullscript/aviary-web to
 * provide backwards compatibility with DS4 components.
 *
 * So, changes should be made in the aviary repo, not here.
 *
 * https://git.fullscript.io/developers/aviary/-/blob/development/packages/aviary-web/src/utils/AviaryProvider/AviaryProvider.tsx
 *
 */
import { AviaryProvider as NewAviaryProvider } from "@fullscript/aviary-web";
import type { ReactNode } from "react";

import { SnackWrap } from "@aviary/components/Snackbar/SnackWrap/SnackWrap";
import { Toaster } from "@aviary/components/Toast/Toaster/Toaster";
import { BreakpointsProvider } from "@shared/context/BreakpointsContext";

import { AviaryGlobalStyles } from "../AviaryGlobalStyles";
import { type ThemeTypes } from "../AviaryThemeContext";
import * as styles from "../materialTheme";

import { ThemeKeyboardShorcut } from "./ThemeKeyboardShortcut";

interface Props {
  /*
   * Toggles a white background instead of muted blue
   */
  isWhiteBackground?: boolean;
  /**
   * Basically the rest of the app
   *
   * @default undefined
   */
  children?: ReactNode;
  /*
   * Disables normalization (ONLY FOR USE WITHIN ADMIN)
   * SERIOUSLY, THIS PAINS ME TO ADD
   * Only pigeons can use this
   */
  noNormalization?: boolean;
  /**
   * Allows to control the theme from a parent component, e.g, Docusaurus, or checkout
   */
  controlledTheme?: ThemeTypes;
  /**
   * Boolean to enable Emerson theme instead of Fullscript
   * Doesn't work with dark mode
   */
  isEmerson?: boolean;
  /**
   * Boolean to use DS3 theme
   *
   * @default false
   */
  isDS3?: boolean;
  /**
   * Boolean whether React has been hydrated . This is mainly used for SSR
   */
  hydrated?: boolean;
}

const AviaryProvider = ({
  isWhiteBackground,
  noNormalization,
  controlledTheme,
  isEmerson,
  isDS3,
  hydrated,
  children,
}: Props) => {
  const isDS4Visuals = isEmerson || !isDS3;
  const isDS3Visuals = !isDS4Visuals;

  return (
    <BreakpointsProvider hydrated={hydrated}>
      <NewAviaryProvider
        brand={isEmerson ? "emerson" : "fullscript"}
        controlledTheme={controlledTheme}
        materialThemeFn={styles.materialAviaryTheme}
        isDS3={isDS3Visuals}
        noNormalization={noNormalization}
      >
        <AviaryGlobalStyles
          isWhiteBackground={isWhiteBackground}
          noNormalization={noNormalization}
        />
        <ThemeKeyboardShorcut {...{ isEmerson, controlledTheme }} />
        <SnackWrap>
          <Toaster>
            <>{children}</>
          </Toaster>
        </SnackWrap>
      </NewAviaryProvider>
    </BreakpointsProvider>
  );
};

export { AviaryProvider, type Props };
